/**
 * Dashboard Page with Metrics and Mini-Charts
 */

import React, { useMemo } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  Skeleton,
  Tooltip,
} from "@mui/material";
import {
  People as PeopleIcon,
  School as SchoolIcon,
  FamilyRestroom as FamilyIcon,
  Class as ClassIcon,
  AccountBalanceWallet as OutstandingIcon,
  Assignment as ApplicationIcon,
  HowToReg as AttendanceIcon,
  ReportProblem as AlertIcon,
  Circle as DotIcon,
  VolunteerActivism as SponsorIcon,
} from "@mui/icons-material";
import { keyframes } from "@mui/system";
import { useNavigate } from "react-router-dom";
import { useDashboardMetrics, useAnalytics, useAuditLog } from "@/hooks/useAdmin";
import { useSponsorFundingConfig } from "@/hooks/useSponsorFunding";
import { useCampuses } from "@/hooks/useCampuses";
import { useFeeTypes } from "@/hooks/useFeeTypes";
import { useAuthStore } from "@/store/authStore";
import { OnboardingChecklist } from "@/components/dashboard/OnboardingChecklist";
import { OverdueAgingCard } from "@/components/dashboard/OverdueAgingCard";
import { CollectionRateCard } from "@/components/dashboard/CollectionRateCard";
import { FirstPayOnTimeCard } from "@/components/dashboard/FirstPayOnTimeCard";
import { FundraisingCard } from "@/components/dashboard/FundraisingCard";
import { MetricCard } from "@/components/dashboard/MetricCard";
import { SchoolGrowthTable } from "@/components/dashboard/SchoolGrowthTable";
import { getApiErrorMessage, getApiErrorCode, getApiErrorStatus, toLocalDateString } from "@/utils/formatters";

interface SectionHeaderProps {
  title: string;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({ title }) => (
  <Box sx={{ mb: 2.5, display: "flex", alignItems: "center", gap: 1.5 }}>
    <Typography
      variant="subtitle2"
      sx={{
        color: "text.secondary",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: 0.8,
        whiteSpace: "nowrap",
      }}
    >
      {title}
    </Typography>
    <Divider sx={{ flex: 1, borderColor: "divider" }} />
  </Box>
);

// Time-of-day greeting for the dashboard hero. Kept as a pure helper so the
// branch boundaries (noon / 6pm, local time) are trivially testable.
export const greetingForHour = (hour: number): string => {
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
};

const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateX(-12px); }
  to { opacity: 1; transform: translateX(0); }
`;

// Color thresholds for attendance
export const getAttendanceColor = (
  rate: number,
  total: number,
  sessionsUnmarked = 0,
): "success" | "warning" | "error" | "info" => {
  // Sessions nobody has marked are work outstanding, whether or not any
  // attendance has been recorded yet. Styling them like "no classes today"
  // was the visual half of the same conflation.
  if (total === 0) return sessionsUnmarked > 0 ? "warning" : "info";
  // The rate outranks the backlog: a 40%-present day with one session still
  // to mark is a bad day first and an unfinished one second, and it used to
  // show amber (change review 2026-09-15, portion 4).
  if (rate < 70) return "error";
  if (rate < 85 || sessionsUnmarked > 0) return "warning";
  return "success";
};

/**
 * The Action Items attendance card.
 *
 * A zero here used to be a single ambiguous state. It could not read "0% — 0
 * of 0 present", which misreads as everyone-absent, and it could not claim
 * "No sessions today", because the rate counts student_attendance ROWS: it
 * cannot tell a day with no classes apart from classes nobody has marked
 * (audit H13). Saying only "no attendance recorded yet" was true but left the
 * office unable to tell which day it was having.
 *
 * The API now reports how many sessions are scheduled and how many of them
 * are unmarked, so the three cases are separable — and they call for
 * different responses:
 *
 *   nothing scheduled       -> nothing to chase
 *   scheduled, none marked  -> chase the teachers
 *   partly marked           -> the rate, plus what is still outstanding
 *
 * Pure helper, so every branch is testable.
 */
export const todaysAttendanceDisplay = (
  rate: number,
  total: number,
  present: number,
  sessions?: number,
  sessionsUnmarked?: number,
): { value: string; subtitle: string } => {
  if (total > 0) {
    const outstanding =
      !sessionsUnmarked
        ? ""
        : sessionsUnmarked === 1
          ? " · 1 session still to mark"
          : ` · ${sessionsUnmarked} sessions still to mark`;
    return {
      value: `${rate}%`,
      subtitle: `${present} of ${total} present${outstanding}`,
    };
  }
  if (sessions === undefined) {
    // An API that predates the session counts. Say only what a record count
    // can support — which is what this card did before, and is still the one
    // honest thing to say without them.
    return { value: "—", subtitle: "No attendance recorded yet" };
  }
  if (sessions > 0) {
    return {
      value: "—",
      subtitle:
        sessions === 1
          ? "1 session to mark today"
          : `${sessions} sessions to mark today`,
    };
  }
  // "sessions" counts only sessions that are still going ahead, so a day whose
  // every class was cancelled reports zero. Saying nothing was scheduled would
  // be untrue on exactly the kind of day someone is checking — a snow day.
  return { value: "—", subtitle: "No classes to mark today" };
};

// Format an ISO date string or YYYY-MM-DD as "May 1" / "May 2026"
const formatShortDate = (raw: unknown): string => {
  if (typeof raw !== "string" || !raw) return "";
  // Append a fixed time so YYYY-MM-DD doesn't get TZ-shifted to the prior day.
  const d = new Date(raw.length === 10 ? `${raw}T12:00:00` : raw);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const formatBillingMonth = (raw: unknown): string => {
  if (typeof raw !== "string" || !raw) return "";
  const d = new Date(raw.length === 10 ? `${raw}T12:00:00` : raw);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
};

// Build a human-readable message from an audit log entry. Entry sources:
// `afterState` is `to_jsonb(NEW)` of the row that changed, so it carries
// raw column names (`total`, not `total_amount`). `familyName` is resolved
// server-side via JOIN — it's not in afterState.
const formatActivityMessage = (
  eventType: string,
  entityType: string,
  afterState: Record<string, unknown>,
  actorName: string | null,
  familyName: string | null,
): string => {
  const s = afterState || {};
  const name = s.first_name ? `${s.first_name} ${s.last_name || ""}`.trim() : (s.name as string) || "";
  const invoiceNum = (s.invoice_number as string) || "";
  // Invoice trigger captures column `total`; payments capture `amount`. Try
  // both to keep one helper across entity types.
  const amount = s.total ?? s.total_amount ?? s.amount;
  const fmtAmt = amount ? `$${Number(amount).toLocaleString("en-US", { minimumFractionDigits: 2 })}` : "";
  const fam = familyName || ((s.family_name as string) || "");
  const className = (s.class_name as string) || "";
  const billingLabel = formatBillingMonth(s.billing_month);
  const dueLabel = formatShortDate(s.due_date);
  const by = actorName ? ` by ${actorName}` : "";
  const forFam = fam ? ` for ${fam}` : "";

  // Entity-specific messages
  if (entityType === "invoices") {
    const status = (s.status as string) || "";
    // Compose: "INV-447 — $250.00 for Smith family · May 2026 (due May 15)"
    const parts: string[] = [];
    if (fmtAmt) parts.push(fmtAmt);
    if (fam) parts.push(`for ${fam}`);
    const meta: string[] = [];
    if (billingLabel) meta.push(billingLabel);
    if (dueLabel) meta.push(`due ${dueLabel}`);
    const tail = [parts.join(" "), meta.join(" · ")].filter(Boolean).join(" · ");
    if (eventType === "INSERT")
      return `Invoice ${invoiceNum} created${tail ? ` - ${tail}` : ""}${by}`;
    // The audit event captures invoice.total at the moment status flipped
    // to 'paid'. That number is gross — later refunds or credit memos can
    // change what the school actually kept. Drop the amount to avoid the
    // misleading "Invoice INV-X paid ($1,025)" reading after a partial
    // refund. The actual money flow is captured by separate Payment and
    // Credit memo events that already render alongside.
    if (status === "paid")
      return `Invoice ${invoiceNum} marked paid${fam ? ` · ${fam}` : ""}`;
    if (status === "void")
      return `Invoice ${invoiceNum} voided${fam ? ` · ${fam}` : ""}${by}`;
    return `Invoice ${invoiceNum || entityType} updated${fam ? ` · ${fam}` : ""}${by}`;
  }
  if (entityType === "payments") {
    if (eventType === "INSERT")
      return `Payment${fmtAmt ? ` of ${fmtAmt}` : ""} received${forFam}`;
    if ((s.status as string) === "refunded")
      return `Refund${fmtAmt ? ` of ${fmtAmt}` : ""} processed${forFam}`;
    return `Payment updated${fmtAmt ? ` (${fmtAmt})` : ""}${forFam}${by}`;
  }
  if (entityType === "students") {
    if (eventType === "INSERT") return `New student ${name} enrolled${forFam}${by}`;
    return `Student ${name || "profile"} updated${forFam}${by}`;
  }
  if (entityType === "families") {
    const fLabel = name || fam;
    if (eventType === "INSERT") return `New family ${fLabel} registered${by}`;
    return `Family ${fLabel || "profile"} updated${by}`;
  }
  if (entityType === "enrollments") {
    const studentName = (s.student_name as string) || name;
    if (eventType === "INSERT") return `${studentName || "Student"} enrolled${className ? ` in ${className}` : ""}${by}`;
    if (eventType === "DELETE") return `${studentName || "Student"} withdrawn${className ? ` from ${className}` : ""}${by}`;
    return `Enrollment updated${studentName ? ` for ${studentName}` : ""}${by}`;
  }
  if (entityType === "classes") {
    const cName = className || (s.name as string) || "";
    if (eventType === "INSERT") return `New class ${cName} created${by}`;
    return `Class ${cName} updated${by}`;
  }
  if (entityType === "credit_memos") {
    return `Credit memo${fmtAmt ? ` of ${fmtAmt}` : ""} applied${invoiceNum ? ` to ${invoiceNum}` : ""}${forFam}${by}`;
  }
  if (entityType === "discount_rules") {
    return `Discount${fmtAmt ? ` of ${fmtAmt}` : ""} ${eventType === "INSERT" ? "added" : "updated"}${name ? ` for ${name}` : ""}${by}`;
  }
  if (entityType === "applications") {
    if (eventType === "INSERT") return `New application submitted${name ? ` for ${name}` : ""}${by}`;
    const status = (s.status as string) || "";
    return `Application${name ? ` for ${name}` : ""} ${status || "updated"}${by}`;
  }
  if (entityType === "guardians") {
    if (eventType === "INSERT") return `New guardian ${name} added${forFam}${by}`;
    return `Guardian ${name || "profile"} updated${forFam}${by}`;
  }
  if (entityType === "users") {
    if (eventType === "INSERT") return `New user ${name} created${by}`;
    return `User ${name || "account"} updated${by}`;
  }

  // Fallback
  const action = eventType === "INSERT" ? "created" : eventType === "DELETE" ? "removed" : "updated";
  const label = entityType.replace(/_/g, " ");
  return `${label} ${action}${name ? ` - ${name}` : ""}${by}`;
};

const getEventColor = (eventType: string): string => {
  if (eventType === "INSERT") return "success";
  if (eventType === "UPDATE") return "info";
  if (eventType === "DELETE") return "error";
  return "grey";
};

const formatTimeAgo = (dateStr: string): string => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

export const DashboardPage: React.FC = () => {
  const { user } = useAuthStore();
  const hasPermission = useAuthStore((state) => state.hasPermission);
  // Campaigns (and their fundraising roll-up) are manager-only, matching the
  // campaigns API's RBAC — gate the card so staff never trigger a 403.
  const canManageCampaigns =
    !!user && ["super_user", "admin", "principal"].includes(user.role);
  const navigate = useNavigate();
  const { data: metrics, isLoading, isError, error } = useDashboardMetrics();
  // Tenant gate for the sponsor receivable card (mig 163)
  const sponsorFunding = useSponsorFundingConfig();
  const { data: campusesData } = useCampuses();
  const { data: feeTypesData } = useFeeTypes();

  // Get date range for last 6 months for sparklines
  const dateRange = useMemo(() => {
    const end = new Date();
    const start = new Date();
    start.setMonth(start.getMonth() - 6);
    return {
      start_date: toLocalDateString(start),
      end_date: toLocalDateString(end),
    };
  }, []);

  const { data: analyticsData } = useAnalytics(dateRange);

  // Recent activity from audit log — fetch more to filter for interesting events
  const { data: auditData, isLoading: auditLoading } = useAuditLog({ limit: 30 });

  // Transform analytics data into sparkline format
  // Client auto-unwraps: { success, data } -> data
  const analytics = analyticsData as {
    revenue_by_month?: Array<{ month: string; invoiced: number; collected: number }>;
    collections_by_month?: Array<{ month: string; total: number }>;
  } | undefined;

  // Invoiced revenue trend (for Outstanding card)
  const invoicedSparkline = useMemo(() => {
    if (!analytics?.revenue_by_month || analytics.revenue_by_month.length < 2) return [];
    return analytics.revenue_by_month.map((item) => ({
      value: item.invoiced,
      label: item.month,
    }));
  }, [analytics]);

  // Collection rate trend (invoiced vs collected ratio per month)
  const collectionRateSparkline = useMemo(() => {
    if (!analytics?.revenue_by_month || analytics.revenue_by_month.length < 2) return [];
    return analytics.revenue_by_month.map((item) => ({
      value: item.invoiced > 0 ? Math.round((item.collected / item.invoiced) * 100) : 0,
      label: item.month,
    }));
  }, [analytics]);

  const formatCurrency = (value: number | undefined) => {
    if (!value) return "$0.00";
    return `$${Number(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatSparklineCurrency = (value: number) => {
    return `$${value.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (isError) {
    const errorMessage = getApiErrorMessage(error, "Failed to load dashboard metrics");
    const errorCode = getApiErrorCode(error);
    const isForbidden = errorCode === "FORBIDDEN" || getApiErrorStatus(error) === 403;

    return (
      <Box sx={{ py: 4 }}>
        <Alert severity={isForbidden ? "warning" : "error"} sx={{ mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom sx={{
            fontWeight: 600
          }}>
            {isForbidden ? "Access Restricted" : "Error Loading Dashboard"}
          </Typography>
          <Typography variant="body2">{errorMessage}</Typography>
          {isForbidden && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Please contact your administrator if you believe you should have
              access to this page.
            </Typography>
          )}
        </Alert>
      </Box>
    );
  }

  // Safety check - if metrics is undefined or has error structure
  if (!metrics || "success" in metrics) {
    const metricsWithError = metrics as { error?: { message?: string } } | undefined;
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="warning">
          <Typography variant="subtitle1" gutterBottom sx={{
            fontWeight: 600
          }}>
            Unable to Load Dashboard
          </Typography>
          <Typography variant="body2">
            {metricsWithError?.error?.message ||
              "No data available. Please try again later."}
          </Typography>
        </Alert>
      </Box>
    );
  }

  // Derived values
  const attendanceRate = metrics?.attendance_rate_today || 0;
  const attendanceTotal = metrics?.attendance_total_today || 0;
  const attendanceColor = getAttendanceColor(
    attendanceRate,
    attendanceTotal,
    metrics?.sessions_unmarked_today || 0,
  );
  const attendanceDisplay = todaysAttendanceDisplay(
    attendanceRate,
    attendanceTotal,
    metrics?.attendance_present_today || 0,
    // Passed through as undefined, not coerced to zero: "the API did not say"
    // and "the API said none" are different claims.
    metrics?.sessions_today,
    metrics?.sessions_unmarked_today,
  );

  // Check if all action items are zero.
  //
  // Unmarked sessions count. Without them the band renders dimmed under the
  // header "All Clear" while the card inside it reads "4 sessions today, none
  // marked yet" — the one state this card was changed to surface.
  //
  // Recorded attendance is only an action item when the rate is below the
  // amber line: a fully marked day at 100% used to keep the band from ever
  // reading "All Clear" (change review 2026-09-15, portion 4).
  const hasActionItems = (metrics?.pending_applications || 0) > 0
    || (metrics?.overdue_accounts || 0) > 0
    || (metrics?.sessions_unmarked_today || 0) > 0
    || (attendanceTotal > 0 && attendanceRate < 85);

  // Audit log items — filter to only show meaningful events
  const allAuditItems = (auditData as { items?: Array<{ id: string; event_type: string; entity_type: string; entity_id: string | null; actor_name: string | null; family_name: string | null; after_state: Record<string, unknown>; created_at: string }> })?.items || [];
  const auditItems = allAuditItems
    .filter((item) => {
      const { event_type: evt, entity_type: ent, after_state: s } = item;
      // Always show: new enrollments, students, families, payments, applications, classes
      if (evt === "INSERT" && ["enrollments", "students", "families", "payments", "applications", "classes"].includes(ent)) return true;
      // Show invoice only when paid or void (not every field update)
      if (ent === "invoices" && evt === "UPDATE" && ["paid", "void"].includes((s?.status as string) || "")) return true;
      // Show new invoices
      if (ent === "invoices" && evt === "INSERT") return true;
      // Show refunds
      if (ent === "payments" && evt === "UPDATE" && (s?.status as string) === "refunded") return true;
      // Show credit memos
      if (ent === "credit_memos") return true;
      // Show application status changes (approved, rejected)
      if (ent === "applications" && evt === "UPDATE" && ["approved", "rejected", "waitlisted"].includes((s?.status as string) || "")) return true;
      // Show deletes (withdrawals, removals)
      if (evt === "DELETE" && ["enrollments", "students"].includes(ent)) return true;
      return false;
    })
    .slice(0, 5);

  return (
    <Box sx={{ maxWidth: 1200 }}>
      {/* Welcome Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="overline"
          sx={{
            color: "text.secondary",
            fontWeight: 600,
            letterSpacing: 1,
            display: "block",
          }}
        >
          {new Date().toLocaleDateString("en-US", {
            weekday: "long",
            month: "long",
            day: "numeric",
            year: "numeric",
          })}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.25 }}>
          {greetingForHour(new Date().getHours())}
          {user?.first_name ? `, ${user.first_name}` : ""}
        </Typography>
        <Typography variant="body1" sx={{ color: "text.secondary", mt: 0.5 }}>
          Here's what's happening with your school today.
        </Typography>
      </Box>
      {/* Onboarding Checklist */}
      <OnboardingChecklist
        metrics={metrics}
        campusCount={Array.isArray(campusesData) ? campusesData.length : 0}
        feeTypeCount={Array.isArray(feeTypesData) ? feeTypesData.length : 0}
      />
      {/* Section 1: Quick Stats */}
      <Box data-section="quick-stats" sx={{ mb: 4 }}>
        <SectionHeader title="Quick Stats" />
        <Grid container spacing={2}>
          <Grid
            size={{
              xs: 6,
              sm: 3
            }}>
            <MetricCard
              title="Students"
              value={metrics?.total_students || 0}
              icon={<SchoolIcon />}
              color="primary"
              subtitle="Active"
              tooltip="Students with at least one active enrollment. Withdrawn or fully-completed students aren't counted."
              onClick={() => navigate("/students")}
            />
          </Grid>
          <Grid
            size={{
              xs: 6,
              sm: 3
            }}>
            <MetricCard
              title="Families"
              value={metrics?.total_families || 0}
              icon={<FamilyIcon />}
              color="success"
              subtitle="Active"
              tooltip="Families with at least one actively enrolled student. A family with only withdrawn students is not counted."
              onClick={() => navigate("/families")}
            />
          </Grid>
          <Grid
            size={{
              xs: 6,
              sm: 3
            }}>
            <MetricCard
              title="Staff"
              value={metrics?.total_staff || 0}
              icon={<PeopleIcon />}
              color="info"
              subtitle="Active"
              tooltip="Active users with the staff, teacher, principal, or admin role."
              // /staff is not a route. Users lists every staff-role account
              // (matching this metric) but needs manage:users; staff-role
              // viewers can open neither Users nor Teachers, so the tile
              // isn't a link for them.
              onClick={
                hasPermission("manage:users") ? () => navigate("/users") : undefined
              }
            />
          </Grid>
          <Grid
            size={{
              xs: 6,
              sm: 3
            }}>
            <MetricCard
              title="Classes"
              value={metrics?.active_classes || 0}
              icon={<ClassIcon />}
              color="warning"
              subtitle="Active"
              tooltip="Active classes that have at least one actively enrolled student. Empty active classes aren't counted."
              onClick={() => navigate("/classes")}
            />
          </Grid>
        </Grid>
      </Box>
      {/* Section 2: Financial Health */}
      <Box data-section="financial" sx={{ mb: 4 }}>
        <SectionHeader title="Financial Health" />
        <Grid container spacing={2}>
          <Grid
            size={{
              xs: 6,
              sm: 3
            }}>
            <MetricCard
              title="Outstanding"
              value={formatCurrency(metrics?.outstanding_balance)}
              icon={<OutstandingIcon />}
              color="warning"
              subtitle="As of today"
              tooltip="All unpaid invoices, including bills that aren't due yet. Sparkline shows the last 6 months of invoiced revenue."
              onClick={() => navigate("/invoices?status=outstanding")}
              sparklineData={invoicedSparkline}
              sparklineColor="warning"
              formatSparklineValue={formatSparklineCurrency}
            />
          </Grid>
          <Grid
            size={{
              xs: 6,
              sm: 3
            }}>
            <OverdueAgingCard
              amount={Number(metrics?.overdue_amount || 0)}
              invoiceCount={metrics?.overdue_invoices_count || 0}
              buckets={metrics?.aging_buckets}
              onClick={() => navigate("/invoices?status=overdue")}
              onBucketClick={(bucket) => navigate(`/invoices?aged=${bucket}`)}
            />
          </Grid>
          <Grid
            size={{
              xs: 6,
              sm: 3
            }}>
            <CollectionRateCard
              rate30d={metrics?.collection_rate_30d}
              rateMtd={metrics?.collection_rate_mtd}
              rateYtd={metrics?.collection_rate_ytd}
              rateAll={metrics?.collection_rate || 0}
              velocity30d={metrics?.cash_velocity_30d}
              velocityMtd={metrics?.cash_velocity_mtd}
              velocityYtd={metrics?.cash_velocity_ytd}
              sparklineData={collectionRateSparkline}
              onClick={() => navigate("/reports/collections")}
            />
          </Grid>
          <Grid
            size={{
              xs: 6,
              sm: 3
            }}>
            <FirstPayOnTimeCard
              rate={metrics?.first_pay_on_time_rate}
              judgeable={metrics?.first_pay_on_time_judgeable}
              onClick={() => navigate("/reports/collections")}
            />
          </Grid>
          {/* Sponsor receivable (mig 163) — what the SPONSOR owes, kept out
              of every family figure above. Tenant-level by construction
              (sponsor line items have NULL enrollment_id, so the campus
              filter cannot scope it) — hence the explicit "school-wide". */}
          {sponsorFunding.enabled && (
            <Grid
              size={{
                xs: 6,
                sm: 3
              }}>
              <MetricCard
                title={`${sponsorFunding.programName} Receivable`}
                value={formatCurrency(metrics?.sponsor_outstanding)}
                icon={<SponsorIcon />}
                color="success"
                subtitle={`School-wide — payable by ${sponsorFunding.sponsorName}`}
                tooltip={`Outstanding on sponsor-funded invoices — money ${sponsorFunding.sponsorName} owes the school. Families owe nothing on these; every other figure in this section is family money only.`}
                onClick={() => navigate("/sponsor-fund")}
              />
            </Grid>
          )}
        </Grid>
      </Box>
      {/* Fundraising — a separate, clearly-labeled ledger from tuition.
          Renders nothing unless a campaign is actively running. */}
      {canManageCampaigns && <FundraisingCard enabled={canManageCampaigns} />}
      {/* Section 3: Action Items - de-emphasized when nothing to act on */}
      <Box data-section="actions" sx={{ mb: 4, opacity: hasActionItems ? 1 : 0.6 }}>
        <SectionHeader title={hasActionItems ? "Action Items" : "Action Items - All Clear"} />
        <Grid container spacing={2}>
          <Grid
            size={{
              xs: 12,
              sm: 4
            }}>
            <MetricCard
              title="Pending Applications"
              value={metrics?.pending_applications || 0}
              icon={<ApplicationIcon />}
              color={metrics?.pending_applications && metrics.pending_applications > 0 ? "warning" : "success"}
              subtitle="Requires review"
              onClick={() => navigate("/applications?status=pending")}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 4
            }}>
            <MetricCard
              title="Today's Attendance"
              value={attendanceDisplay.value}
              icon={<AttendanceIcon />}
              color={attendanceColor}
              subtitle={attendanceDisplay.subtitle}
              onClick={() => navigate("/attendance")}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 4
            }}>
            <MetricCard
              title="Overdue Accounts"
              value={metrics?.overdue_accounts || 0}
              icon={<AlertIcon />}
              color={metrics?.overdue_accounts && metrics.overdue_accounts > 0 ? "error" : "success"}
              subtitle="Families with overdue invoices"
              onClick={() => navigate("/reports/ar-aging")}
            />
          </Grid>
        </Grid>
      </Box>
      {/* Section 4: School Growth + Recent Activity side by side */}
      <Grid container spacing={3}>
        {/* Growth Table */}
        {metrics?.growth_stats && (
          <Grid
            size={{
              xs: 12,
              md: 8
            }}>
            <Box data-section="growth" sx={{ mb: 4 }}>
              <SectionHeader title="School Growth" />
              <SchoolGrowthTable growth={metrics.growth_stats} />
            </Box>
          </Grid>
        )}

        {/* Recent Activity */}
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <Box data-section="activity" sx={{ mb: 4 }}>
            <SectionHeader title="Recent Activity" />
            <Card sx={{ boxShadow: 1, maxHeight: 220, overflow: "hidden" }}>
              <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
                {auditLoading ? (
                  <Box sx={{ p: 2 }}>
                    {[...Array(4)].map((_, i) => (
                      <Box key={i} sx={{ display: "flex", gap: 1, mb: 1.5 }}>
                        <Skeleton variant="circular" width={8} height={8} sx={{ mt: 0.5 }} />
                        <Box sx={{ flex: 1 }}>
                          <Skeleton variant="text" width="80%" height={16} />
                          <Skeleton variant="text" width="50%" height={14} />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : auditItems.length === 0 ? (
                  <Box sx={{ p: 2, textAlign: "center" }}>
                    <Typography variant="caption" sx={{
                      color: "text.secondary"
                    }}>
                      No recent activity
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ py: 0.5 }}>
                    {auditItems.map((item, idx) => {
                      // Compute the human-readable activity message once per
                      // row -- used both as the tooltip title and the
                      // visible (potentially truncated) text. Was an inline
                      // IIFE; lifted out for readability.
                      const message = formatActivityMessage(
                        item.event_type,
                        item.entity_type,
                        item.after_state,
                        item.actor_name,
                        item.family_name,
                      );
                      const timeAgo = formatTimeAgo(item.created_at);
                      const isLast = idx === auditItems.length - 1;
                      return (
                        <Box
                          key={item.id}
                          sx={{
                            display: "flex",
                            gap: 1,
                            px: 2,
                            py: 0.75,
                            animation: `${fadeSlideIn} 0.3s ease-out ${idx * 0.05}s both`,
                            "&:hover": { bgcolor: "action.hover" },
                            cursor: "pointer",
                            borderBottom: isLast ? "none" : "1px solid",
                            borderColor: "divider",
                          }}
                          onClick={() => navigate("/admin/audit-log")}
                          tabIndex={0}
                          role="button"
                          aria-label={`View audit log entry: ${message}, ${timeAgo}`}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              navigate("/admin/audit-log");
                            }
                          }}
                        >
                          <DotIcon
                            sx={{
                              fontSize: 8,
                              mt: 0.7,
                              color: `${getEventColor(item.event_type)}.main`,
                              flexShrink: 0,
                            }}
                          />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Tooltip title={message} arrow placement="top" enterDelay={300}>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: "0.7rem",
                                  display: "block",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  // Wrap on a phone: a hover tooltip is a poor
                                  // way to recover a truncated sentence on a
                                  // touch screen, and reflow must not cost
                                  // information (WCAG 1.4.10).
                                  whiteSpace: { xs: "normal", sm: "nowrap" },
                                  color: "text.primary",
                                }}
                              >
                                {message}
                              </Typography>
                            </Tooltip>
                            {/* Word gap between the two block lines, so the
                                row's text matches its label (WCAG 2.5.3). */}
                            {" "}
                            <Typography
                              variant="caption"
                              sx={{
                                color: "text.secondary",
                                fontSize: "0.6rem"
                              }}>
                              {timeAgo}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
